import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Link, Stack, Typography, useTheme } from '@mui/material'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'

import { Button } from '@repo/ui'

import { PageContainer, PageHeader, Term } from 'components/UI'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { helpArticle } from 'help/articles'
import { usePushPage } from 'hooks/usePushPage'
import { ROUTES } from 'routes'
import { font, space } from 'settings/tokens'

import { helpArticlePath } from './navState'

/**
 * One Help article.
 *
 * ## The markdown component map is COPIED from `LegalDocumentDrawer`, deliberately
 *
 * That file hard-codes sizes rather than using the theme's variants, and its own note
 * says why: this app's `body2` is 12px, which is too small for long-form prose read on a
 * 375px phone, and `h4` is 12px below `sm` — SMALLER than the body text under it, so
 * section headings read as barely-there. The same two problems apply here for the same
 * reason (a page of real prose rather than UI chrome), so the same resolved values are
 * used rather than re-derived. If the two ever need to diverge, that is the moment to
 * extract a shared markdown renderer (working-conventions §36); today they agree, and one
 * copy that says where it came from is cheaper than a component with two callers and no
 * disagreement to resolve.
 *
 * ## No skeleton
 *
 * The article corpus is a committed artifact imported as a module, so it is present in
 * the first render. §19 governs async surfaces; there is nothing to wait for here.
 */
export const HelpArticlePage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? helpArticle(slug) : undefined

  // Always back to the index, never `navigate(-1)`. The article is reached from the Help
  // list AND from a TermSheet's "Learn more" on some other page entirely, and in the
  // second case "back" to that page would skip Help, which is where a reader who has just
  // been given one answer is most likely to want to go next.
  const { pageSx, goBack } = usePushPage(ROUTES.HELP, { pushOnly: true })

  return (
    <Stack
      sx={{
        ...pageSx,
        background: theme.palette.background.paper,
        minHeight: '100vh',
        pb: { xs: 4, md: 8 },
      }}
    >
      <PageContainer>
        <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
          <PageHeader
            backLabel='Help'
            onBack={goBack}
            title={article ? article.title : 'Article not found'}
          />

          {article ? (
            <ReactMarkdown
              /*
                Without this, the `term:` scheme never arrives. react-markdown's
                `defaultUrlTransform` allows `http|https|ircs|mailto|xmpp` and rewrites
                anything else to the EMPTY STRING — so `[owner](term:owner)` would reach the
                `a` map below with `href=''`, fall through the term branch AND the external
                branch, and land in the bare-slug branch, which would navigate to
                `/help/undefined`. A dead end with no error, from a reference that looks
                correct in the markdown. Widened by exactly one inert scheme; everything else
                still goes through the default.
              */
              urlTransform={url => (url.startsWith('term:') ? url : defaultUrlTransform(url))}
              components={{
                h3: ({ children }) => (
                  <Typography
                    variant='h4'
                    sx={{
                      fontSize: `${font.heading}px`,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: theme.palette.text.primary,
                      mt: `${space.xxl}px`,
                      mb: `${space.md}px`,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                // The corpus authors sections as `###`, but a hand-edited article could
                // use either, and a heading that silently rendered at 12px is the exact
                // defect the map exists to fix. Both map to the same treatment.
                h4: ({ children }) => (
                  <Typography
                    variant='h4'
                    sx={{
                      fontSize: `${font.heading}px`,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: theme.palette.text.primary,
                      mt: `${space.xxl}px`,
                      mb: `${space.md}px`,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                p: ({ children }) => (
                  <Typography
                    variant='body2'
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: `${font.body}px`,
                      lineHeight: 1.6,
                      mb: 2,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                /*
                  Plain `Box component='ul'`, NOT MUI's `List`/`ListItem`, and this is a
                  bug fix rather than a preference.
                  `List` carries a themed override, `.MuiList-root li { list-style-type:
                  none }`, which is a direct declaration on the `li`. The `disc` set on the
                  `ul` only ever reaches the `li` by INHERITANCE, and inheritance loses to
                  any direct declaration regardless of specificity — so every bullet
                  rendered as a bare indent. Measured in the browser: `ul` computed `disc`
                  while its `li` computed `none`, which is exactly what that mismatch looks
                  like from the outside and is easy to misread as the `ul` rule not applying.
                  `Box` emits the bare element and never matches that selector, so the
                  markers come back with no `!important` anywhere. It is also what
                  `LegalDocumentDrawer` already does for the same markdown.
                */
                ul: ({ children }) => (
                  <Box component='ul' sx={{ listStyleType: 'disc', pl: 3, my: 0, mb: 2 }}>
                    {children}
                  </Box>
                ),
                ol: ({ children }) => (
                  <Box component='ol' sx={{ listStyleType: 'decimal', pl: 3, my: 0, mb: 2 }}>
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Typography
                    component='li'
                    variant='body2'
                    sx={{
                      display: 'list-item',
                      color: theme.palette.text.secondary,
                      fontSize: `${font.body}px`,
                      lineHeight: 1.6,
                      mb: 1,
                    }}
                  >
                    {children}
                  </Typography>
                ),
                strong: ({ children }) => (
                  <Box component='span' sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {children}
                  </Box>
                ),
                /*
                  Article-to-article links are written as BARE SLUGS in the corpus —
                  `[If the tag will not scan](tag-will-not-scan)` in "What verified means".
                  So they are ROUTED, not opened: this app runs inside a shadow root on a
                  `MemoryRouter`, where an `<a href>` would be resolved against the HOST
                  page's address and leave the app entirely.

                  An absolute `href` is left as a real external link. Nothing in the corpus
                  uses one today; the branch exists because the failure of getting it wrong
                  (a `https://` routed into the SPA and 404ing) is one nobody would trace
                  back to this map.
                */
                /*
                  Three destinations, tested in this order. `term:` FIRST, because it is the
                  only one that does not navigate at all — it opens the definition sheet over
                  the article the reader is already in.
                */
                a: ({ children, href }) =>
                  href?.startsWith('term:') ? (
                    <Term id={href.slice('term:'.length)}>{children}</Term>
                  ) : href && /^https?:\/\//i.test(href) ? (
                    <Link href={href} target='_blank' rel='noopener noreferrer' color='inherit'>
                      {children}
                    </Link>
                  ) : (
                    <Link
                      component='button'
                      type='button'
                      color='inherit'
                      onClick={() => href && navigate(helpArticlePath(href))}
                      sx={{ font: 'inherit', p: 0, border: 'none', background: 'none' }}
                    >
                      {children}
                    </Link>
                  ),
              }}
            >
              {article.body}
            </ReactMarkdown>
          ) : (
            /*
              An unknown slug renders THIS rather than throwing. It is reachable by a stale
              deep link from a TermSheet after an article is renamed, and a crash there
              takes the whole app down through the ErrorBoundary — a dead end is bad, a
              white screen is worse.
            */
            <Stack spacing={2} alignItems='flex-start'>
              <Typography sx={{ fontSize: font.body, color: theme.palette.text.primary }}>
                We could not find that article.
              </Typography>
              <Typography sx={{ fontSize: font.label, color: theme.palette.text.secondary }}>
                It may have been renamed. Everything else is still on the Help page.
              </Typography>
              {/* An explicit way out as well as the header's back-link. The two are the
                  same destination, and on a page whose whole content is an apology the
                  reader should not have to find the small one at the top. */}
              <Button variant='secondary' onClick={goBack}>
                Back to Help
              </Button>
            </Stack>
          )}
        </Box>
      </PageContainer>
    </Stack>
  )
}

export default HelpArticlePage
